import { useMemo, useCallback } from 'react';
import {
  useController,
  FieldPath,
  FieldValues,
  RegisterOptions,
  useFormContext,
  PathValue,
} from 'react-hook-form';

import { SystemPathIds, SystemSelect } from '../system-select';

export interface SystemSelectInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name?: TName;
  constructionTypeIdName?: FieldPath<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, TName>;
  constructionTypeIdOptions?: string[];
}

export const SystemSelectInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name = 'systemIds' as TName,
  constructionTypeIdName = 'constructionTypeId' as FieldPath<TFieldValues>,
  rules,
  constructionTypeIdOptions = [],
}: SystemSelectInputProps<TFieldValues, TName>) => {
  const form = useFormContext<TFieldValues>();

  // Use controller for systemIds
  const {
    field: { value: currentlySystemValues, onChange: onSystemIdsChange },
    fieldState: { error: fieldError },
    formState: { disabled },
  } = useController({
    name,
    control: form.control,
    rules,
    defaultValue: [] as PathValue<TFieldValues, TName>,
  });

  // Use controller for constructionTypeId
  const {
    field: { value: constructionTypeId, onChange: onConstructionTypeIdChange },
  } = useController({
    name: constructionTypeIdName,
    control: form.control,
  });

  const systemPathIds = useMemo(() => {
    if (!constructionTypeId) return [] as SystemPathIds;
    return [constructionTypeId, ...currentlySystemValues] as unknown as SystemPathIds;
  }, [constructionTypeId, currentlySystemValues]);

  const handleOnClear = useCallback(() => {
    onSystemIdsChange([]);
    onConstructionTypeIdChange('');
  }, [onSystemIdsChange, onConstructionTypeIdChange]);

  const handleOnChange = useCallback(
    (systemPathIds: SystemPathIds) => {
      const [constructionTypeId, ...systemPaths] = systemPathIds;
      onConstructionTypeIdChange(constructionTypeId);
      onSystemIdsChange(systemPaths);
    },
    [onSystemIdsChange, onConstructionTypeIdChange],
  );

  return (
    <SystemSelect
      systemPathIds={systemPathIds}
      onChange={handleOnChange}
      onClear={handleOnClear}
      error={fieldError?.message}
      disabled={disabled}
      constructionTypeIdOptions={constructionTypeIdOptions}
    />
  );
};
